/**
 * Canonical field keys and scope helpers for SystemConfig (Vuex + upsertConfig).
 * Use `taxConfigScope`, `statementConfigScope`, `resaleConfigScope`, and `globalScope`
 * so each `calledScope` stays explicit.
 */

import { cloneItem } from './vue-global';

/** System Config → Global Setting form (GlobalSetting.vue) */
export const GLOBAL_SETTING_FORM_FIELD_KEYS = [
  'id',
  'countyName',
  'countyNumber',
  'treasurerPhone',
  'treasurerFax',
  'treasurerEmail',
  'treasurerWebsite',
  'treasurerName',
  'countyClerkName',
  'countyAssessorName',
  'exciseChairman',
  'exciseViceChairman',
  'exciseMember',
  'commissionerChairman',
  'commissionerViceChairman',
  'commissionerMember',
  'businessDate',
  'globalTitle',
  'hoursOfOperation',
  'FederalId',
  'addressAdd1',
  'addressAdd2',
  'addressAdd3',
  'addressCity',
  'addressState',
  'addressZip',
  'sherrifAddressAdd1',
  'sherrifAddressAdd2',
  'sherrifAddressAdd3',
  'sherrifAddressCity',
  'sherrifAddressState',
  'sherrifAddressZip',
  'sherrifPhone',
  'convenienceFee',
  'penalty',
  'forteApiURL',
  'payrixApiUrl',
  'useNewTaxUnitSetup',
  'conEverythingToCaps',
  'disablePasswordAutoFill',
  'useAtlasSearch',
  'useWkstNoFromUser',
  'scanByWkst',
  'enableTmSupportOption',
  'miscellaneous',
  'mtgTax',
  'od',
  'feeWaiverMiscellaneous',
  'feeWaiverMtgTax',
  'feeWaiverOd',
  'feeWaiverTax',
];

/** User Administration → Users list toolbar (UsersList.vue) */
export const USERS_LIST_GLOBAL_FIELD_KEYS = [
  'id',
  'logoutTimePeriod',
  'passExpPeriod',
  'minPasswordLength',
];

/**
 * resaleConfig — resaleSetting.vue plus fields from tax workflows
 * (BrowseTrWorkFile / ResalePropertyList).
 */
export const RESALE_CONFIG_FIELD_KEYS = [
  'id',
  'resaleSaleYear',
  'resaleSaleDate',
  'resaleSaleLocation',
  'resaleSaleLocationAddOne',
  'lienDate',
  'resalePublicationDate',
  'resalePenaltyDate',
  'nameOfNewspaper',
  'resaleTaxes',
  'resalePenalty',
  'resaleFees',
  'resaleSA',
  'noticePostedBy',
  'resaleAddressee',
  'resaleDisplayTotalDue',
  'resalePrintAddressAtTop',
  'resaleIncludeMobileHomeDisclaimer',
  'resaleTaxDeedLine',
  'resalePaymentOptionsLine',
  'resaleOptionsLine1',
  'resaleOptionsLine1Flag',
  'resaleOptionsLine2',
  'resaleOptionsLine2Flag',
  'resaleOptionsLine3',
  'resaleOptionsLine3Flag',
  'resaleOptionsLine4',
  'resaleOptionsLine4Flag',
  'resaleUseCertificateLienYear',
];

/**
 * taxConfig — TaxSetting.vue + PostingPeriod.vue (posting period / prev uncollected).
 */
export const TAX_CONFIG_FIELD_KEYS = [
  'id',
  'penaltyDate',
  'penaltyRate',
  'taxYear',
  'halfPayAmountThreshold',
  'overUnderFee',
  'pennyRoundingFee',
  'enablePennyRounding',
  'sherifFee',
  'treasurerFee',
  'publicationFee',
  'overUnderLimit',
  'sherifFeeAmount',
  'treasurerFeeAmount',
  'publicationFeeAmount',
  'isAutoAddAlDelqToPTW',
  'staCode',
  'ptwStatusCodes',
  'certificateFee',
  'postage',
  'returnReceiptFee',
  'restrictedDeliveryFee',
  'curPostPerFromDate',
  'curPostPerToDate',
  'prevUncollTaxAmt',
  'newTaxRollDate',
  'newTaxRollAmt',
  'pidValue',
  'isDoPidEntry',
  'disableAddressChangeOnPickList',
  'useTaxRecordAdditionName',
  'allowEditsForSA',
  'specialAssesment',
  'omittedProperties',
  'saFee',
  'saFeeAmount',
  'nsfFee',
  'nsfFeeAmount',
  'autoAdd417OnNsfPayments',
  'webPaymentNo',
  'taxWarrantLetterFormat',
  'taxWarrantPaymentOptionsLine1',
  'taxWarrantPaymentOptionsLine2',
  'taxWarrantPaymentOptionsLine3',
  'taxWarrantLetterPrintTwoCopies',
  'printSignedDateLineOnPTW',
  'updateOwnerChangesAdjFlag',
  'updateOwnerChangesAdjType',
  'updateTaxrollChangesAdjFlag',
  'updateTaxrollChangesAdjType',
  'makeReasonCodeRequired',
  'restrictAdjSavingOnTaxAmtNtMatching',
  'additionOnInfoShet',
  'printBaseIdOnSA',
  'replaceOwnerNameOnAssessorUpdates',
  'addPercentToCareOfNamesFromAssessor',
  'taxInquiryDefaultYearScope',
  'assessorCompany',
  // Internal support reminders about this county's yearly roll. TM admins only - the server
  // refuses a value change from anyone else, so it stays in this list purely so a county user
  // saving the Tax tab sends the stored text back unchanged rather than dropping the key.
  'newTaxRollNotes',
];

/**
 * statementConfig — StatementsSetting.vue + BrowseTrWorkFileFooter (middle / if-paid-by fields).
 */
export const STATEMENT_CONFIG_FIELD_KEYS = [
  'id',
  'pidValue',
  'printBarcodeTop',
  'printBarcodeMiddle',
  'printBarcodeBottom',
  'printBarcodeNone',
  'printQRTop',
  'printQRMiddle',
  'printQRBottom',
  'printQRNone',
  'printScanlineTop',
  'printScanlineMiddle',
  'printScanlineBottom',
  'printScanlineNone',
  'scanline',
  'titleSelection',
  'applyToInfoSheet',
  'showUsername',
  'noHalfPayAfterDec31',
  'printGridAndMessage',
  'treasurerCountyNameColor',
  'countyNameColor',
  'treasurerNameColor',
  'includeDelinq',
  'appendAdditionToLegalDesc',
  'includeAcreageOrLotsInSitus',
  'includeSitusAndLegalOnAllStubs',
  'retainThisStub',
  'highlightCountyName',
  'skipTreasurerNameOnTopStub',
  'skipParcelId',
  'skipOwnerNumber',
  'omitSitusFromLegalDesc',
  'includeAddressChangeBox',
  'printTaxBecomesDelinquent',
  'printWMDeputyLine',
  'retainThisStubText',
  'addressChangeBoxText',
  'taxBecomesDelinquentText',
  'wmDeputyLineText',
  'msgForFirstHalfOrFull',
  'dateForFirstHalfOrFull',
  'msgForSecondHalf',
  'dateForSecondHalf',
  'msgForFull',
  'dateForFull',
  'taxUnderPayBoxInMiddle',
  'taxUnderPayBoxInBottom',
  'taxUnderPayBoxInFinal',
  'printMsgInMiddle',
  'hideHeadingsInMiddle',
  'printIfPaidByGrid',
  'printIfPaidByGridDate1',
  'printIfPaidByGridDate2',
  'printIfPaidByGridDate3',
  'middleSectionPrint',
  'ifPaidBy1',
  'ifPaidBy2',
  'ifPaidBy3',
  'middleMsgForNormal',
  'middleMsgForDelinquent',
  'middleMsgForUnderGrid',
  'middleMsgForNextToGrid',
  'savedNormalMessages',
  'savedDelinquentMessages',
  'savedUnderGridMessages',
  'savedNextToGridMessages',
  'bccEmailAddress',
  'subjectLine',
  'subjectBody',
];

const pickKeys = (source, keys) => {
  const out = {};
  keys.forEach((key) => {
    out[key] = source[key];
  });
  return out;
};

const buildSpecificConfigItem = (id, values = {}) => {
  const item = { id };
  Object.keys(values).forEach((key) => {
    item[`configuration.${key}`] = values[key];
  });
  return item;
};

export const GLOBAL_SPECIFIC_CONFIG_FIELDS = {
  businessDate: 'businessDate',
};

export const PAYMENT_SPECIFIC_CONFIG_FIELDS = {
  latestPostDate: 'latestPostDate',
};

export const TAX_SPECIFIC_CONFIG_FIELDS = {
  taxYear: 'taxYear',
  newTaxRollDate: 'newTaxRollDate',
  newTaxRollAmt: 'newTaxRollAmt',
  newTaxRollNotes: 'newTaxRollNotes',
};

export const pickGlobalSettingFormFields = (source = {}) => pickKeys(
  source,
  GLOBAL_SETTING_FORM_FIELD_KEYS,
);

export const pickUsersListGlobalFields = (source = {}) => pickKeys(
  source,
  USERS_LIST_GLOBAL_FIELD_KEYS,
);

export const pickResaleConfigFields = (source = {}) => pickKeys(
  source,
  RESALE_CONFIG_FIELD_KEYS,
);

export const pickTaxConfigFields = (source = {}) => pickKeys(
  source,
  TAX_CONFIG_FIELD_KEYS,
);

export const pickStatementConfigFields = (source = {}) => pickKeys(
  source,
  STATEMENT_CONFIG_FIELD_KEYS,
);

/** `global` scope — field maps for `calledScope: 'global'` (pick only; merge in components). */
export const globalScope = {
  scope: 'global',
  settingFormFieldKeys: GLOBAL_SETTING_FORM_FIELD_KEYS,
  pickSettingFormFields: pickGlobalSettingFormFields,
  usersListFieldKeys: USERS_LIST_GLOBAL_FIELD_KEYS,
  pickUsersListFields: pickUsersListGlobalFields,
  specificFields: GLOBAL_SPECIFIC_CONFIG_FIELDS,
  buildSpecificItem(id, values = {}) {
    return buildSpecificConfigItem(id, values);
  },
};

/** `resaleConfig` scope */
export const resaleConfigScope = {
  scope: 'resaleConfig',
  fieldKeys: RESALE_CONFIG_FIELD_KEYS,
  pickFields: pickResaleConfigFields,
  mergeForPersist(source = {}, overrides = {}) {
    return {
      ...cloneItem(source),
      ...pickResaleConfigFields(source),
      ...overrides,
    };
  },
};

/** `taxConfig` scope */
export const taxConfigScope = {
  scope: 'taxConfig',
  fieldKeys: TAX_CONFIG_FIELD_KEYS,
  pickFields: pickTaxConfigFields,
  specificFields: TAX_SPECIFIC_CONFIG_FIELDS,
  mergeForPersist(source = {}, overrides = {}) {
    return {
      ...cloneItem(source),
      ...pickTaxConfigFields(source),
      ...overrides,
    };
  },
  buildSpecificItem(id, values = {}) {
    return buildSpecificConfigItem(id, values);
  },
};

/** `statementConfig` scope */
export const statementConfigScope = {
  scope: 'statementConfig',
  fieldKeys: STATEMENT_CONFIG_FIELD_KEYS,
  pickFields: pickStatementConfigFields,
  mergeForPersist(source = {}, overrides = {}) {
    return {
      ...cloneItem(source),
      ...pickStatementConfigFields(source),
      ...overrides,
    };
  },
};

/** `paymentConfig` scope — used for specific-field updates from payment workflows. */
export const paymentConfigScope = {
  scope: 'paymentConfig',
  specificFields: PAYMENT_SPECIFIC_CONFIG_FIELDS,
  buildSpecificItem(id, values = {}) {
    return buildSpecificConfigItem(id, values);
  },
};
